/**
 * BaseTile - Abstract base class for all tile types in the CA system
 *
 * This class defines the interface that all tiles must implement,
 * including rendering, movement logic, cloning, and state management.
 */

import Phaser from "phaser";
import { GAME_CONFIG } from "../config";
import { isEmpty } from "../constants/gameConstants";
import { Coordinates, TileAndEntity, TileIndex, Vector } from "../types";
import { Direction } from "../utils/Direction";
import { GlobalState } from "../systems/GlobalState";

/**
 * Describes how a tile should be rendered
 */
export type TextureDescriptor =
  | { type: "png"; path: string }
  | { type: "procedural"; generator: (scene: Phaser.Scene, key: string) => void };

/**
 * Result of calling move() on an entity
 *
 * MoveResults encode both movement and game interactions.
 * The AutomataCoordinator handles each type appropriately.
 */

// Basic movement
export type MoveResult_Move = { type: "move"; direction: Direction };
export type MoveResult_Wall = { type: "wall" };
export type MoveResult_Wait = { type: "wait" };

// Entity interactions
export type MoveResult_Die = { type: "die"; who: "self" | "other" | "both"; collisionWith: "tile" | "entity"; direction: Direction };
export type MoveResult_Push = { type: "push"; direction: Direction, withMomentum?: boolean; };

// Collectibles
export type MoveResult_CollectStar = { type: "collectStar"; direction: Direction };
export type MoveResult_CollectKey = { type: "collectKey"; direction: Direction; keyColor: string };
export type MoveResult_CollectFlippers = { type: "collectFlippers"; direction: Direction };
export type MoveResult_CollectSuctionBoots = { type: "collectSuctionBoots"; direction: Direction };

// Doors
export type MoveResult_OpenDoor = { type: "openDoor"; direction: Direction; doorColor: string };

// Buttons
export type MoveResult_PressButton = { type: "pressButton"; direction: Direction; }
export type MoveResult_ReleaseButton = { type: "releaseButton"; direction: Direction; }

// Rotation
export type MoveResult_TriggerRotation = { type: "triggerRotation"; direction: Direction };

// Hazards
export type MoveResult_Burn = { type: "burn"; direction: Direction };
export type MoveResult_Drown = { type: "drown"; direction: Direction };

// Consumable tiles
export type MoveResult_StepOnSand = { type: "stepOnSand"; direction: Direction };

// Hint tiles
export type MoveResult_ShowHint = { type: "showHint"; direction: Direction };
export type MoveResult_HideHint = { type: "hideHint"; direction: Direction };

// Level completion
export type MoveResult_Win = { type: "win"; direction: Direction };

// Union of all MoveResult types
export type MoveResult =
  | MoveResult_Move
  | MoveResult_Wall
  | MoveResult_Wait
  | MoveResult_Die
  | MoveResult_Push
  | MoveResult_CollectStar
  | MoveResult_CollectKey
  | MoveResult_CollectFlippers
  | MoveResult_CollectSuctionBoots
  | MoveResult_OpenDoor
  | MoveResult_PressButton
  | MoveResult_ReleaseButton
  | MoveResult_TriggerRotation
  | MoveResult_Burn
  | MoveResult_Drown
  | MoveResult_StepOnSand
  | MoveResult_ShowHint
  | MoveResult_HideHint
  | MoveResult_Win;

/**
 * Read-only context provided to move() function
 * Contains only what's needed to query tiles and timing information
 */
export class MoveContext {
  private getTileAt: (col: number, row: number) => TileIndex | null;
  private getEntityAt: (col: number, row: number) => TileIndex | null;
  private getPlayerPosition?: () => Coordinates | null;
  readonly globalState: GlobalState;
  readonly deltaMs: number; // Time since last tick in milliseconds

  constructor(
    getTileAt: (col: number, row: number) => TileIndex | null,
    getEntityAt: (col: number, row: number) => TileIndex | null,
    deltaMs: number,
    globalState: any,
    getPlayerPosition?: () => Coordinates | null
  ) {
    this.getTileAt = getTileAt;
    this.getEntityAt = getEntityAt;
    this.deltaMs = deltaMs;
    this.globalState = globalState;
    this.getPlayerPosition = getPlayerPosition;
  }

  getAtDirection(
    coordinates: Coordinates,
    direction: Direction
  ): TileAndEntity {
    switch (direction.value) {
      case "left":
        return this.getAt({ col: coordinates.col - 1, row: coordinates.row });
      case "right":
        return this.getAt({ col: coordinates.col + 1, row: coordinates.row });
      case "up":
        return this.getAt({ col: coordinates.col, row: coordinates.row - 1 });
      case "down":
        return this.getAt({ col: coordinates.col, row: coordinates.row + 1 });
    }
  }

  getAt(coordinates: Coordinates): TileAndEntity {
    return {
      tile: this.getTileAt(coordinates.col, coordinates.row),
      entity: this.getEntityAt(coordinates.col, coordinates.row),
    };
  }

  getPlayer(): Coordinates | null {
    if (this.getPlayerPosition) {
      return this.getPlayerPosition();
    }
    return null;
  }

  left(coordinates: Coordinates): TileAndEntity {
    return this.getAt({ col: coordinates.col - 1, row: coordinates.row });
  }

  right(coordinates: Coordinates): TileAndEntity {
    return this.getAt({ col: coordinates.col + 1, row: coordinates.row });
  }

  up(coordinates: Coordinates): TileAndEntity {
    return this.getAt({ col: coordinates.col, row: coordinates.row - 1 });
  }

  down(coordinates: Coordinates): TileAndEntity {
    return this.getAt({ col: coordinates.col, row: coordinates.row + 1 });
  }
}

/**
 * Abstract base class for all tiles (both static and entities)
 */
export abstract class BaseTile {
  // Immutable identity
  readonly id: string; // Unique identifier for tracking

  // Grid position
  col: number;
  row: number;

  // Visual representation (Phaser sprite reference) - private backing field
  private _sprite?: Phaser.GameObjects.Sprite;

  constructor(id: string, col: number, row: number) {
    this.id = id;
    this.col = col;
    this.row = row;
  }

  /**
   * Get the sprite for this entity
   */
  get sprite(): Phaser.GameObjects.Sprite | undefined {
    return this._sprite;
  }

  /**
   * Set the sprite for this entity and establish bidirectional link
   */
  set sprite(sprite: Phaser.GameObjects.Sprite | undefined) {
    this._sprite = sprite;
    // Establish bidirectional link
    if (sprite) {
      sprite.setData("entity", this);
    }
  }

  coordsAtDirection(
    direction: Direction
  ): Coordinates {
    const coordinates = this.coordinates();
    switch (direction.value) {
      case "left":
        return { col: coordinates.col - 1, row: coordinates.row };
      case "right":
        return { col: coordinates.col + 1, row: coordinates.row };
      case "up":
        return { col: coordinates.col, row: coordinates.row - 1 };
      case "down":
        return { col: coordinates.col, row: coordinates.row + 1 };
    }
  }

  /**
   * Destroy the sprite and clean up references
   */
  destroySprite(): void {
    if (this._sprite) {
      this._sprite.destroy();
      this._sprite = undefined;
    }
  }

  coordinates(): Coordinates {
    return { col: this.col, row: this.row };
  }

  /**
   * Get the screen x-coordinate (in pixels) for this tile's center
   */
  x(): number {
    return this.col * GAME_CONFIG.tileSize + GAME_CONFIG.tileSize / 2;
  }

  /**
   * Get the screen y-coordinate (in pixels) for this tile's center
   */
  y(): number {
    return this.row * GAME_CONFIG.tileSize + GAME_CONFIG.tileSize / 2;
  }

  /**
   * Move this tile in the specified direction
   * Updates col and row based on the direction vector
   */
  moveTo(direction: Direction): void {
    const offset = direction.toVector();
    this.col += offset.dx;
    this.row += offset.dy;
  }

  /**
   * Helper: Check if target is out of bounds
   */
  protected isOutOfBounds(target: TileAndEntity): boolean {
    return target.tile === null;
  }

  /**
   * Helper: Check if target has an entity
   */
  protected hasEntity(target: TileAndEntity): boolean {
    return !isEmpty(target.entity);
  }

  /**
   * Get the tile type for this tile (e.g., TILE.WALL, TILE.BEE_HORIZONTAL)
   * Derived from tile state rather than stored.
   */
  abstract tileType(): TileIndex;

  /**
   * Define how this tile should be rendered
   * Returns either a PNG path or a procedural generator function
   */
  abstract textureDescriptor(): TextureDescriptor;

  /**
   * Get the texture key for this tile
   * Derives the key from the texture descriptor
   */
  textureKey(): string {
    const descriptor = this.textureDescriptor();

    if (descriptor.type === "png") {
      // For PNG textures, use the path without extension as the key
      return descriptor.path.replace(/\.(png|jpg|jpeg)$/i, "");
    } else {
      // For procedural textures, use tile type as key
      return `tile-${this.tileType()}`;
    }
  }

  /**
   * Returns true if this tile is a movable entity, false if static
   * Entities override to return true, static tiles use default false
   */
  isEntity(): boolean {
    return false; // Default: most tiles are static
  }

  /**
   * Called every tick to determine what action this tile should take.
   * Examines neighbors via context and returns a decision.
   * Most static tiles use the default implementation (wait/do nothing).
   *
   * @param context - Read-only view of the game state
   * @returns MoveResult indicating what action to take
   */
  move(context: MoveContext): MoveResult {
    return { type: "wait" }; // Default: tiles don't move
  }

  /**
   * Creates a deep copy of this tile for pass-by-value semantics.
   * Must copy all mutable state to avoid shared references.
   *
   * @returns A new instance with the same state
   */
  abstract clone(): BaseTile;

  /**
   * Serializes tile metadata to a plain object for debugging/saving.
   *
   * @returns Plain object with tile state
   */
  abstract toJSON(): Record<string, any>;
}
